/*
 * @fileOverView Tabs component definition
 */
import EventBus from 'utils/EventBus';
import QueryString from 'utils/QueryString';

/**
 * A reference to the attributes specific to this view.
 *
 * @property ATTRIBUTES
 * @type {Object}
 * @private
 */
const ATTRIBUTES = {
  'TAB_ID': 'data-tab-id',
  'CONTENT_ID': 'data-tab-content-id'
}

export default {
  name: 'tabs',

  props: {
    'defaultTab': {
      'type': String,
      'default': ''
    }
  },

  data: function () {
    return {
      activeTab: ''
    }
  },

  computed: {
    selectedIndex: function () {
      // Keeps child dropdowns in sync with selected tab.
      return this.getEnabledTabIndex(this.activeTab);
    }
  },

  beforeMount() {
    // Called before mount to ensure a deep-linked tab is open before anything
    // inside of it that may be deep linked (such as an accordion) gets mounted.
    this.deepLink();
  },

  mounted() {
    if (this.defaultTab) {
      this.activeTab = this.defaultTab;
    }

    // The first enabled tab is active unless another has been deep linked.
    if (!this.activeTab) {
      this.activeTab = document.querySelector('.tabs-button:not([disabled])').parentNode.getAttribute(ATTRIBUTES.TAB_ID);
    }

    this.interceptDeepLinkClicks();
  },

  methods: {
    /**
     * Opens a directly-linked item.
     *
     * The item must contain a `data-tab-content-id` attribute value that
     * matches the value of a `tab` URL query parameter, such as
     * `?tab=marketing-digital-strategy`.
     */
    deepLink() {
      const linkedTab = document.querySelector(`[${ATTRIBUTES.CONTENT_ID}="${QueryString('tab')}"]`);

      if (linkedTab) {
        this.activeTab = linkedTab.getAttribute(ATTRIBUTES.CONTENT_ID);
      }
    },

    /**
     * Reacts to a change by a child dropdown element.
     *
     * @param  {Number} index - The index of the item selected in the dropdown.
     */
    dropdownChanged(index) {
      // Update the active tab to match the selected dropdown item.
      if (index !== this.getEnabledTabIndex(this.activeTab)) {
        this.activeTab = this.getTabIdByEnabledIndex(index);
      }
    },

    handleClick(slug) {
      this.activeTab = slug;

      const $form = $(this.$el).closest('form');

      if ($form && $form.hasClass('js-validation-performed') && $form.validate()) {
        this.$nextTick(() => {
          $form.valid();
        })
      }

      // Trigger a resize for any tab content constructed with libraries that
      // react to resizing, such as the Gallery (bLazy and Masonry).
      $(window).trigger('resize');
    },

    /**
     * Intercepts clicks on any deep links within the tab content.
     */
    interceptDeepLinkClicks() {
      const contentDeepLinks = this.$el.querySelectorAll('a[href^="?tab=');

      for (let i = 0; i < contentDeepLinks.length; i++) {
        contentDeepLinks[i].addEventListener('click', this.handleDeepLinkClick);
      }
    },

    /**
     * Handles deep link clicks within tab content to avoid a page refresh.
     *
     * An example of where this is useful is the Glossary.
     */
    handleDeepLinkClick(e) {
      // Update the URL.
      history.pushState(history.state, '', e.target.href);

      // Switch tabs if necessary.
      this.deepLink();

      // Other components within the tab content may handle deep links within
      // their content, such as accordions. Let them react.
      EventBus.$emit('history-state-pushed');

      // Don't let the page refresh, of course.
      e.preventDefault();
    },

    /**
     * Retrieves the enabled index of a tab by slug value.
     *
     * Necessary because the dropdown excludes disabled items and communicates
     * by indexed keys.
     */
    getEnabledTabIndex(slug) {
      const activeTabButtons = document.querySelectorAll('.tabs-button:not([disabled])');
      for (let i = 0; i < activeTabButtons.length; i++) {
        if (activeTabButtons[i].parentNode.getAttribute(ATTRIBUTES.TAB_ID) === slug) {
          return i;
        }
      }
    },

    /**
     * Retrieves the slug of a tab given the enabled index.
     *
     * Necessary because the dropdown excludes disabled items and communicates
     * by indexed keys.
     */
    getTabIdByEnabledIndex(enabledIndex) {
      const activeTabButtons = document.querySelectorAll('.tabs-button:not([disabled])');
      if (activeTabButtons.length > enabledIndex) {
        return activeTabButtons[enabledIndex].parentNode.getAttribute(ATTRIBUTES.TAB_ID);
      }
    }
  }
};
